import {
  Grid,
  Skeleton,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
} from '@mui/material';

interface TextSkeletonProps {
  variant: TypographyProps['variant'];
  width: number | string;
  sx?: SxProps<Theme>;
  testId?: string;
}

/**
 * One line of text as it will render: the same Typography, so the line box
 * (and the loaded text's height) is the same. The skeleton is an
 * inline-block aligned to the top: its wave clips (overflow hidden), which
 * would move its baseline to its bottom edge and grow the line.
 */
export function TextSkeleton({
  variant,
  width,
  sx,
  testId,
}: TextSkeletonProps): JSX.Element {
  return (
    <Typography variant={variant} sx={sx} data-testid={testId}>
      <Skeleton
        width={width}
        sx={{ display: 'inline-block', verticalAlign: 'top' }}
      />
    </Typography>
  );
}

interface WarehouseDetailTitleSkeletonProps {
  /** The document modal has a type line under its number. */
  subtitle?: boolean;
}

/** WarehouseDetailDialog's title slot: the h5 title and its subtitle. */
export function WarehouseDetailTitleSkeleton({
  subtitle = false,
}: WarehouseDetailTitleSkeletonProps): JSX.Element {
  return (
    <>
      <TextSkeleton
        variant="h5"
        width={220}
        testId="warehouse-detail-title-skeleton"
      />
      {subtitle && <TextSkeleton variant="body2" width={140} />}
    </>
  );
}

/** A WarehouseBadge (soft Label) in the dialog's header. */
export function WarehouseDetailBadgeSkeleton({
  width,
}: {
  width: number;
}): JSX.Element {
  return (
    <Skeleton
      variant="rounded"
      width={width}
      height={24}
      sx={{ borderRadius: 0.75 }}
    />
  );
}

/** A footer action button, left of "Zamknij". */
export function WarehouseDetailActionSkeleton({
  width,
}: {
  width: number;
}): JSX.Element {
  return <Skeleton variant="rounded" width={width} height={36} />;
}

interface WarehouseDetailFieldsSkeletonProps {
  /** How many label/value pairs the common case shows. */
  fields: number;
  /** The loaded grid's `md` column span. */
  md: number;
  sx?: SxProps<Theme>;
}

/** A detail grid of caption labels over body2 values. */
export function WarehouseDetailFieldsSkeleton({
  fields,
  md,
  sx,
}: WarehouseDetailFieldsSkeletonProps): JSX.Element {
  return (
    <Grid container spacing={2} sx={sx}>
      {Array.from({ length: fields }, (_, index) => (
        <Grid key={index} item xs={12} sm={6} md={md}>
          <TextSkeleton variant="caption" width={90} />
          <TextSkeleton variant="body2" width={150} />
        </Grid>
      ))}
    </Grid>
  );
}
